import { ObjectTools } from '../../tools/object-tools';
import { SolText } from './sol-text';
import { SolTranslation } from './sol-translation';
import { SolMarkdownFeatures } from '../../tools/sol-content-markdown-tools';
import { NavigationTarget } from '../../tools/router-tools';

export type SolTextBoxCollapsedHeight = 'none' | '64' | '128' | '256';

const DEFAULT_COLLAPSED_HEIGHT: SolTextBoxCollapsedHeight = '128';

export class SolTextBox {
  private constructor(
    public readonly text: SolText | undefined,
    public readonly translations: SolTranslation[],
    public readonly collapsedHeight: SolTextBoxCollapsedHeight,
    public readonly centered: boolean,
    public readonly markdown: string | undefined,
    public readonly markdownFeatures: Partial<SolMarkdownFeatures> | undefined,
    public readonly linkTarget: NavigationTarget,
  ) {}

  public static empty(): SolTextBox {
    return SolTextBox.of(SolText.empty());
  }

  public static of(text: SolText): SolTextBox {
    return new SolTextBox(text, [], DEFAULT_COLLAPSED_HEIGHT, false, undefined, undefined, NavigationTarget.SameTab);
  }

  public static ofTranslations(translations: SolTranslation[]): SolTextBox {
    return new SolTextBox(
      undefined,
      translations,
      DEFAULT_COLLAPSED_HEIGHT,
      false,
      undefined,
      undefined,
      NavigationTarget.SameTab,
    );
  }

  public static ofMarkdown(
    markdown: string,
    features?: Partial<SolMarkdownFeatures>,
    linkTarget: NavigationTarget = NavigationTarget.SameTab,
  ): SolTextBox {
    return new SolTextBox(undefined, [], DEFAULT_COLLAPSED_HEIGHT, false, markdown, features, linkTarget);
  }

  public withText(text: SolText): SolTextBox {
    return this.copy({ text, translations: [] });
  }

  public withTranslations(translations: SolTranslation[]): SolTextBox {
    return this.copy({ translations, text: SolText.empty() });
  }

  public withCollapsedHeight(collapsedHeight: SolTextBoxCollapsedHeight): SolTextBox {
    return this.copy({ collapsedHeight });
  }

  public withCentered(centered: boolean): SolTextBox {
    return this.copy({ centered });
  }

  private copy(props: Partial<SolTextBox>): SolTextBox {
    return ObjectTools.copy<SolTextBox>(this, props);
  }
}
